<template>
    <div class="point-settings-container">
        <o-bread-crumb>
            <template v-slot:pageName>Point System Settings</template>
            <template v-slot:pageDetail>Configuration</template>
            <template v-slot:pageDescription><br></template>
        </o-bread-crumb>

        <FeatureGate featureKey="point_control">
        <div v-if="validation" class="alert alert-danger" role="alert">
            <i class="fas fa-exclamation-circle me-2"></i>{{ validation }}
        </div>

        <!-- Main Card -->
        <div v-if="isPointSystemActive" class="point-settings-card">
            <!-- Header Section -->
            <div class="point-settings-header">
                <div class="header-left">
                    <h4 class="header-title">
                        <i class="fas fa-cog me-2 primary-text"></i>Point System Configuration
                    </h4>
                    <p class="header-subtitle">Manage point system mode and settings</p>
                </div>
            </div>

            <!-- Content Section -->
            <div class="point-settings-content">
                <div v-if="page.isLoading" class="loading-state">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    <p class="loading-text">Loading settings...</p>
                </div>

                <div v-else>
                    <!-- Mode Toggle Section -->
                    <div class="settings-section">
                        <div class="section-header">
                            <span class="section-number">01</span>
                            <div>
                                <label class="section-label">Point System Mode</label>
                                <p class="section-desc">Choose how point plans are managed across your organization</p>
                            </div>
                        </div>
                        <div class="mode-toggle-cards">
                                <div 
                                    class="mode-card"
                                    :class="{ 'active': isCentralize }"
                                    @click="selectMode(true)"
                                >
                                <div class="active-indicator">
                                    <div class="indicator-dot"></div>
                                </div>
                                <img src="/img/admin-icon/corporate-icon.svg" alt="Corporate" class="mode-icon" />
                                <strong>Centralize</strong>
                                <p>Point Plan managed at Corporate level</p>
                            </div>
                            <div class="mode-card" :class="{ 'active': !isCentralize }" @click="selectMode(false)">
                                <div class="active-indicator">
                                    <div class="indicator-dot"></div>
                                </div>
                                <img src="/img/admin-icon/merchant-icon.svg" alt="Merchant" class="mode-icon" />
                                <strong>Decentralize</strong>
                                <p>Each Merchant has own Point Plan</p>
                            </div>
                        </div>

                        <!-- Centralize info -->
                        <div class="info-section-footer" v-if="isCentralize">
                            <div class="alert alert-info">
                                <i class="fas fa-info-circle me-2"></i>
                                <strong>Centralize Mode:</strong> Point Plan will be managed at Corporate level.
                                Please go to <router-link to="/point/plan" class="primary-text">Point Plan</router-link>
                                menu to configure.
                            </div>
                        </div>
                    </div>

                    <div class="section-divider"></div>

                    <!-- Point Value Settings -->
                    <div class="settings-section">
                        <div class="section-header">
                            <span class="section-number">02</span>
                            <div>
                                <label class="section-label">Point Value Settings</label>
                                <p class="section-desc">Configure how point values are converted and displayed</p>
                            </div>
                        </div>

                        <div class="value-settings-box">
                            <div class="setting-row">
                                <div class="setting-row-info">
                                    <span class="setting-row-title">Enable Point Value Conversion</span>
                                    <span class="setting-row-desc">Allow points to be converted to monetary value</span>
                                </div>
                                <div class="form-check form-switch">
                                    <input class="form-check-input" type="checkbox" v-model="pointValueEnabled"
                                        id="pointValueSwitch">
                                </div>
                            </div>

                            <div v-if="pointValueEnabled">
                                <div class="setting-divider"></div>
                                <div class="setting-row align-items-start">
                                    <div class="setting-row-info">
                                        <label class="setting-row-title" for="pointValueRate">Point Value Rate
                                            (IDR)</label>
                                        <span class="setting-row-desc">Example: 1.000 means 1 point = Rp 1,000. Changes
                                            will update all member point value when saved.</span>
                                    </div>
                                    <div class="point-value-input-group">
                                          <o-input-text 
                                            name="point_value_rate" 
                                            v-model="pointValueRate" 
                                            type="text"
                                            mask="idr"
                                            placeholder="1.000"
                                            :autocomplete="'off'"
                                            :max="15"
                                        />
                                    </div>
                                </div>

                                <div class="setting-divider"></div>
                                <div class="setting-row">
                                    <div class="setting-row-info">
                                        <span class="setting-row-title">Show Point Value to Members</span>
                                        <span class="setting-row-desc">If disabled, the point value conversion rate will
                                            be hidden from the member dashboard, but still active for internal
                                            calculation.</span>
                                    </div>
                                    <div class="form-check form-switch">
                                        <input class="form-check-input" type="checkbox" id="pointValueVisibleSwitch"
                                            v-model="pointValueVisible" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>


                </div>
            </div>
        </div>

        <!-- Sticky Unsaved Changes Bar -->
        <transition name="slide-up">
            <div v-if="hasUnsavedChanges" class="unsaved-bar">
                <div class="unsaved-bar-inner">
                    <div class="unsaved-bar-left">
                        <div class="unsaved-bar-icon">
                            <i class="fas fa-exclamation-triangle"></i>
                        </div>
                        <div class="unsaved-bar-text">
                            <strong>You have unsaved changes</strong>
                            <span v-if="changesList.length > 0">Changes to be saved:</span>
                            <ul v-if="changesList.length > 0">
                                <li v-for="(change, index) in changesList" :key="index">{{ change }}</li>
                            </ul>
                        </div>
                    </div>
                    <div class="unsaved-bar-actions">
                        <button @click="cancelChanges" :disabled="page.isSaving" class="btn-discard">
                            Discard
                        </button>
                        <button @click="saveAllChanges" :disabled="page.isSaving" class="btn-save">
                            <i v-if="page.isSaving" class="fas fa-spinner fa-spin me-1"></i>
                            <i v-else class="fas fa-save me-1"></i>
                            <span>{{ page.isSaving ? 'Saving...' : 'Save Changes' }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Point Plan Warning Modal -->
        <o-warning-box
            v-if="showPointPlanWarning"
            title="Point Plan Not Configured"
            message="You are switching to Centralize mode, but this Corporate doesn't have a Point Plan yet. Please configure the Point Plan first."
            action-text="Go to Point Plan"
            cancel-text="Close"
            @action="goToPointPlan"
            @cancel="closePointPlanWarning"
        />
        </FeatureGate>
    </div>
</template>

<script>
    import { onMounted, reactive, ref, computed, watch } from 'vue'
    import { useStore } from 'vuex'
    import { useRouter } from 'vue-router'
    import { OBreadCrumb, OInputText, OWarningBox } from '@/components/OmnihControl'
    import FeatureGate from '@/components/upsell/FeatureGate.vue'
    import { usePointSystem } from '@/composables/pointsystem'
    import { useToast } from '@/composables/useToast'

    export default {
        name: 'PointSystemSettings',
        components: {
            OBreadCrumb,
            OInputText,
            OWarningBox,
            FeatureGate
        },
        setup() {
            const store = useStore()
            const router = useRouter()
            const { showToast } = useToast()
            const { isPointSystemActive, pointControlFlag, metadata } = usePointSystem()

            const page = reactive({
                isLoading: false,
                isSaving: false
            })

            const isCentralize = ref(false)
            const pendingMode = ref(null)
            const isConfirmVisible = ref(false)
            const showPointPlanWarning = ref(false)
            const pointValueEnabled = ref(false)
            const pointValueRate = ref('')
            const pointValueVisible = ref(false)
            const validation = ref('')
            const corporateId = ref(null)
            const isInitialLoad = ref(true)

            const originalSettings = ref({
                mode: 'DECENTRALIZE',
                pointValueEnabled: false,
                pointValueRate: '',
                pointValueVisible: false
            })

            const hasUnsavedChanges = computed(() => {
                if (page.isLoading || isInitialLoad.value) return false
                const current = {
                    mode: isCentralize.value ? 'CENTRALIZE' : 'DECENTRALIZE',
                    pointValueEnabled: pointValueEnabled.value,
                    pointValueRate: pointValueRate.value,
                    pointValueVisible: pointValueVisible.value
                }
                return JSON.stringify(current) !== JSON.stringify(originalSettings.value)
            })


            const changesList = computed(() => {
                const changes = []

                const currentMode = isCentralize.value ? 'CENTRALIZE' : 'DECENTRALIZE'
                if (currentMode !== originalSettings.value.mode) {
                    changes.push(`Switch Point System Mode to ${currentMode}`)
                }

                if (pointValueEnabled.value !== originalSettings.value.pointValueEnabled) {
                    changes.push(`Point Value Conversion is ${pointValueEnabled.value ? 'Enabled' : 'Disabled'}`)
                }

                if (pointValueRate.value !== originalSettings.value.pointValueRate) {
                    changes.push(`Point Value Rate set to Rp. ${pointValueRate.value} /point`)
                }

                if (pointValueVisible.value !== originalSettings.value.pointValueVisible) {
                    changes.push(`Show Point Value to Members: ${pointValueVisible.value ? 'Enabled' : 'Disabled'}`)
                }

                return changes
            })

            onMounted(() => {
                loadBusinessProfile()
            })

            watch(pointValueRate, (newVal) => {
                if (!newVal) return

                const strVal = String(newVal)
                if (strVal.startsWith('0') && strVal.length > 1) {
                    pointValueRate.value = strVal.replace(/^0+/, '') || '0'
                }
            })

            watch(pointValueEnabled, (val) => {
                if (!val) {
                    pointValueRate.value = ''
                    pointValueVisible.value = false
                }
            })

            async function loadBusinessProfile() {
                try {
                    const businessProfile = await store.dispatch('Auth/getBusinessProfile')
                    corporateId.value = businessProfile.corporate_id

                    if (corporateId.value) {
                        await loadFeatureFlag()
                    }
                } catch (error) {
                    validation.value = error.response?.data?.message || 'Failed to load business profile'
                }
            }

            async function checkPointPlan() {
                if (!isCentralize.value) return

                try {
                    store.commit('Pointplan/clear_filter')

                    if (corporateId.value) {
                        store.commit('Pointplan/add_filter', { filter: { corporate_id: corporateId.value } })
                    }

                    await store.dispatch('Pointplan/read')

                    const plans = store.state.Pointplan.pointplans || []

                    if (plans.length === 0) {
                        showPointPlanWarning.value = true
                    }
                } catch (error) {
                    console.error('Failed to check point plans', error)
                }
            }

            async function loadFeatureFlag() {
                page.isLoading = true
                validation.value = ''

                try {
                    await store.dispatch('FeatureFlag/fetchFeatureFlag', corporateId.value)

                    const flag = pointControlFlag.value
                    isCentralize.value = flag === 'CENTRALIZE'

                    pointValueEnabled.value = metadata.value?.point_value_enabled === true || metadata.value?.point_value_enabled === 1
                    pointValueRate.value = metadata.value?.point_value_rate ? priceFormat(metadata.value.point_value_rate) : ''
                    pointValueVisible.value = metadata.value?.point_value_visible_to_member ?? false

                    originalSettings.value = {
                        mode: flag || 'DECENTRALIZE',
                        pointValueEnabled: pointValueEnabled.value,
                        pointValueRate: pointValueRate.value,
                        pointValueVisible: pointValueVisible.value
                    }

                    setTimeout(() => {
                        isInitialLoad.value = false
                    }, 100)

                    if (isCentralize.value) {
                        checkPointPlan()
                    }

                } catch (error) {
                    isCentralize.value = false
                } finally {
                    page.isLoading = false
                }
            }

            function selectMode(centralize) {
                if (centralize === isCentralize.value) return
                isCentralize.value = centralize
            }

            function goToPointPlan() {
                showPointPlanWarning.value = false
                router.push('/point/plan')
            }

            function closePointPlanWarning() {
                showPointPlanWarning.value = false
            }

            function priceFormat(value) {
                if (!value) return ''
                return value.toString()
                    .split('.')[0]
                    .replace(/[^0-9]/g,'')
                    .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
            }



            async function saveAllChanges() {
                if (!hasUnsavedChanges.value) return

                page.isSaving = true
                validation.value = ''

                try {
                    const currentMode = isCentralize.value ? 'CENTRALIZE' : 'DECENTRALIZE'
                    const rateValue = pointValueRate.value ? Number(pointValueRate.value.toString().split('.').join('')) : 0

                    if (pointValueEnabled.value && rateValue <= 0) {
                        showToast({
                            type: 'error',
                            title: 'Validation Error',
                            message: 'Point Value Rate must be greater than 0.'
                        })
                        return
                    }

                    await store.dispatch('FeatureFlag/updateFeatureFlag', {
                        corporateId: corporateId.value,
                        feature: 'point_control',
                        flag: currentMode,
                        metadata: {
                            point_value_enabled: pointValueEnabled.value,
                            point_value_rate: rateValue,
                            point_value_visible_to_member: pointValueVisible.value
                        }
                    })

                    originalSettings.value = {
                        mode: currentMode,
                        pointValueEnabled: pointValueEnabled.value,
                        pointValueRate: pointValueRate.value,
                        pointValueVisible: pointValueVisible.value
                    }

                    showToast({
                        type: 'success',
                        title: 'Settings Saved',
                        message: 'All changes have been saved successfully.',
                    })



                    // Check Point Plan after saving if Centralize is active
                    if (isCentralize.value) {
                        checkPointPlan()
                    }

                } catch (error) {
                    validation.value = error.response?.data?.message || 'Failed to save settings'
                    showToast({
                        type: 'error',
                        title: 'Save Failed',
                        message: validation.value,
                    })
                } finally {
                    page.isSaving = false
                }
            }

            function cancelChanges() {
                if (!hasUnsavedChanges.value) return

                if (originalSettings.value.mode === 'CENTRALIZE') {
                    isCentralize.value = true
                } else {
                    isCentralize.value = false
                }
                pointValueEnabled.value = originalSettings.value.pointValueEnabled
                pointValueRate.value = originalSettings.value.pointValueRate
                pointValueVisible.value = originalSettings.value.pointValueVisible

                showToast({
                    type: 'info',
                    title: 'Changes Cancelled',
                    message: 'All changes have been discarded.',
                })
            }

        return {
            page,
            isCentralize,
            isConfirmVisible,
            pendingMode,
            pointValueEnabled,
            pointValueRate,
            pointValueVisible,
            validation,
            isPointSystemActive,
            hasUnsavedChanges,
            changesList,
            selectMode,
            saveAllChanges,
            cancelChanges,
            showPointPlanWarning,
            goToPointPlan,
            closePointPlanWarning,
        }
    }
}
</script>

<style scoped>
    /* Feature Disabled Container */

    .feature-disabled-container {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 400px;
        background: #ffffff;
        border-radius: 12px;
        border: 1px solid #e5e7eb;
        padding: 2rem;
    }

    .disabled-content {
        text-align: center;
        color: #6b7280;
    }

    .disabled-content h4 {
        color: #374151;
        margin-bottom: 0.5rem;
        font-weight: 600;
    }

    .disabled-content p {
        margin: 0;
        font-size: 0.95rem;
    }

    /* Container */
    .point-settings-container {
        padding: 16px;
        padding-right: 20px;
        max-width: 100%;
    }

    /* Main Card */
    .point-settings-card {
        background: #ffffff;
        border-radius: 12px;
        overflow: hidden;
        border: 1px solid #e5e7eb;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
    }

    /* Header */
    .point-settings-header {
        display: flex;
        align-items: center;
        padding: 1.5rem 2rem;
        border-bottom: 1px solid #e5e7eb;
        background: linear-gradient(135deg, #fafafa 0%, #ffffff 100%);
    }

    .header-left {
        flex: 1;
    }

    .header-title {
        font-size: 1.5rem;
        font-weight: 600;
        color: #111827;
        margin: 0 0 0.25rem 0;
        display: flex;
        align-items: center;
    }

    .header-subtitle {
        font-size: 0.875rem;
        color: #6b7280;
        margin: 0;
    }

    /* Content */
    .point-settings-content {
        padding: 2rem;
    }

    /* Loading State */
    .loading-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 4rem 2rem;
    }

    .loading-text {
        margin-top: 1rem;
        color: #6b7280;
        font-size: 0.875rem;
    }

    /* Settings Section */
    .settings-section {
        margin-bottom: 2rem;
    }

    .section-header {
        display: flex;
        align-items: flex-start;
        gap: 1rem;
        margin-bottom: 1.25rem;
    }

    .section-number {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border-radius: 8px;
        background: color-mix(in srgb, var(--primary-color, #3b82f6), white 88%);
        color: var(--primary-color, #3b82f6);
        font-size: 0.75rem;
        font-weight: 700;
        flex-shrink: 0;
        margin-top: 2px;
    }

    .section-label {
        display: block;
        font-size: 0.9375rem;
        font-weight: 700;
        color: #111827;
        margin-bottom: 0.2rem;
    }

    .section-desc {
        font-size: 0.8125rem;
        color: #9ca3af;
        margin: 0;
    }

    .section-divider {
        height: 1px;
        background: #f3f4f6;
        margin: 0 0 2rem 0;
    }

    /* Mode Toggle Cards */
    .mode-toggle-cards {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        gap: 1rem;
    }

    .mode-card {
        position: relative;
        background: #ffffff;
        border: 2px solid #e5e7eb;
        border-radius: 12px;
        padding: 1.5rem;
        cursor: pointer;
        transition: all 0.2s ease;
        text-align: center;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
    }

    .mode-card:hover {
        border-color: var(--primary-color, #3b82f6);
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        transform: translateY(-2px);
    }

    .mode-card.active {
        border-color: var(--primary-color, #3b82f6);
        background: color-mix(in srgb, var(--primary-color, #3b82f6), white 93%);
        box-shadow: 0 4px 6px -1px color-mix(in srgb, var(--primary-color, #3b82f6), transparent 80%);
    }

    .mode-card .mode-icon {
        width: 160px;
        height: 160px;
        display: block;
        margin: 0 auto;
        filter: grayscale(100%);
        opacity: 0.5;
        transition: all 0.2s ease;
    }

    .mode-card.active .mode-icon {
        filter: grayscale(0%);
        opacity: 1;
    }

    .mode-card strong {
        display: block;
        font-size: 1.125rem;
        color: #6b7280;
        margin-bottom: 0.5rem;
        transition: color 0.2s ease;
    }

    .mode-card.active strong {
        color: var(--primary-color, #3b82f6);
    }

    .mode-card p {
        font-size: 0.8rem;
        color: #6b7280;
        margin: 0;
        line-height: 1.4;
    }

    .active-indicator {
        position: absolute;
        top: 1rem;
        left: 1rem;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        border: 2px solid #d1d5db;
        background-color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
    }

    .mode-card.active .active-indicator {
        border-color: var(--primary-color, #3b82f6);
    }

    .indicator-dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background-color: var(--primary-color, #3b82f6);
        transform: scale(0);
        transition: all 0.2s ease;
    }

    .mode-card.active .indicator-dot {
        transform: scale(1);
    }

    /* Value Settings Box */
    .value-settings-box {
        background: #f9fafb;
        border: 1px solid #e5e7eb;
        border-radius: 12px;
        overflow: hidden;
    }

    .setting-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 2rem;
        padding: 1.125rem 1.5rem;
    }

    .setting-row-info {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        flex: 1;
    }

    .setting-row-title {
        font-size: 0.9rem;
        font-weight: 600;
        color: #111827;
    }

    .setting-row-desc {
        font-size: 0.8rem;
        color: #9ca3af;
        line-height: 1.5;
    }

    .setting-divider {
        height: 1px;
        background: #f0f0f0;
        margin: 0;
    }

    .point-value-input-group {
        width: 160px;
        flex-shrink: 0;
    }

    .form-check-input:checked {
        background-color: var(--primary-color, #3b82f6);
        border-color: var(--primary-color, #3b82f6);
    }

    .form-check-input:focus {
        box-shadow: 0 0 0 0.2rem color-mix(in srgb, var(--primary-color, #3b82f6), transparent 75%);
    }

    .form-control:focus {
        border-color: var(--primary-color, #3b82f6);
        box-shadow: 0 0 0 0.2rem color-mix(in srgb, var(--primary-color, #3b82f6), transparent 75%);
    }

    /* Info Footer*/
    .info-section-footer {
        margin-top: 1.5rem;
    }

    .info-section-footer .alert {
        margin: 0;
        border-radius: 10px;
    }

    /* Sticky Unsaved Bar */
    .unsaved-bar {
        position: fixed;
        bottom: 0;
        left: 230px;
        right: 0;
        z-index: 9999;
        background: #1f2937;
        border-top: 1px solid #374151;
        box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.18);
        padding: 0.875rem 6.5rem 0.875rem 1.5rem;
    }

    .unsaved-bar-inner {
        max-width: 1400px;
        margin: 0 auto;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1.5rem;
    }

    .unsaved-bar-left {
        display: flex;
        align-items: flex-start;
        gap: 0.875rem;
        flex: 1;
        min-width: 0;
    }

    .unsaved-bar-icon {
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 8px;
        background: rgba(234, 179, 8, 0.15);
        color: #eab308;
        font-size: 0.875rem;
        flex-shrink: 0;
        margin-top: 2px;
    }

    .unsaved-bar-text {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        min-width: 0;
    }

    .unsaved-bar-text strong {
        color: #f9fafb;
        font-size: 0.9rem;
    }

    .unsaved-bar-text span {
        color: #9ca3af;
        font-size: 0.8rem;
    }

    .unsaved-bar-text ul {
        list-style: none;
        padding: 0;
        margin: 0.25rem 0 0;
        display: flex;
        flex-wrap: wrap;
        gap: 0.375rem;
    }

    .unsaved-bar-text ul li {
        font-size: 0.75rem;
        color: #d1d5db;
        background: rgba(255, 255, 255, 0.07);
        border-radius: 6px;
        padding: 0.2rem 0.6rem;
    }

    .unsaved-bar-actions {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        flex-shrink: 0;
    }

    .btn-discard {
        background: transparent;
        color: #9ca3af;
        border: 1px solid #374151;
        padding: 0.5rem 1.125rem;
        border-radius: 8px;
        font-size: 0.875rem;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        white-space: nowrap;
    }

    .btn-discard:hover:not(:disabled) {
        background: #374151;
        color: #f9fafb;
    }

    .btn-discard:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .btn-save {
        background: var(--primary-color, #3b82f6);
        color: white;
        border: none;
        padding: 0.5rem 1.25rem;
        border-radius: 8px;
        font-size: 0.875rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        white-space: nowrap;
        user-select: none;
        position: relative;
        z-index: 10000;
        box-shadow: 0 2px 8px color-mix(in srgb, var(--primary-color, #3b82f6), transparent 60%);
    }

    .btn-save i,
    .btn-save span,
    .btn-discard i,
    .btn-discard span {
        pointer-events: none;
    }

    .btn-save:hover:not(:disabled) {
        filter: brightness(1.1);
        transform: translateY(-1px);
    }

    .btn-save:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }

    /* Slide-up Transition*/
    .slide-up-enter-active,
    .slide-up-leave-active {
        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
    }

    .slide-up-enter-from,
    .slide-up-leave-to {
        transform: translateY(100%);
        opacity: 0;
    }

    /* Responsive*/
    @media (max-width: 768px) {
        .point-settings-header {
            padding: 1.25rem;
        }

        .point-settings-content {
            padding: 1.25rem;
        }

        .mode-toggle-cards {
            grid-template-columns: 1fr;
        }

        .setting-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
        }

        .point-value-input-group {
            width: 100%;
        }

        .unsaved-bar-inner {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
        }

        .unsaved-bar-actions {
            width: 100%;
            justify-content: flex-end;
        }
    }
</style>

<style>
    #sidebarMenu.collapse~* .unsaved-bar,
    body:has(#sidebarMenu.collapse) .unsaved-bar {
        left: 60px;
    }

    @media (max-width: 767.98px) {
        .unsaved-bar {
            left: 0 !important;
        }
    }
</style>